import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Pencil } from 'lucide-react';

import Button from '../../../components/ui/Button';

// Son inceleme M7 — tek düzenleyici: taslak soruları yalnız oluşturma/düzenleme sayfasında (ölçek sorusu, bölüm, olumsuz
// ifade, öne çıkar orada) düzenlenir. Bu sekme soruları gösterir; taslakta "Soruları Düzenle" düzenleyiciyi adım 1'de açar.

const Container = styled.div`
  padding: 0 4px;
  height: 100%;
  overflow-y: auto;
`;

const QuestionCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  border-left: 3px solid ${({ $color }) => $color || '#0291EF'};
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const QuestionIndex = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  min-width: 24px;
  padding-top: 2px;
`;

const QuestionText = styled.div`
  flex: 1;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
`;

const OptionList = styled.div`
  padding-left: 34px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionItem = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OptionDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#94a3b8'};
  flex-shrink: 0;
`;

const SectionTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 16px 0 8px;
  padding-left: 34px;
`;

const Empty = styled.div`
  text-align: center;
  padding: 40px;
  color: #A0A8B4;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
`;

const typeColors = {
  singleChoice: '#2563eb',
  singleChoiceAlternate: '#3b82f6',
  multipleChoice: '#16a34a',
  multipleChoiceAlternate: '#ca8a04',
  ranking: '#dc2626',
  freeText: '#9333ea',
};

const TYPES_WITH_OPTIONS = ['singleChoice', 'singleChoiceAlternate', 'multipleChoice', 'multipleChoiceAlternate', 'ranking'];

export default function SurveyQuestionsTab({ surveyId, questions, isDraft }) {
  const { t } = useTranslation();
  const history = useHistory();

  const editAction = isDraft && (
    <div style={{ textAlign: 'center', marginTop: 12 }}>
      <Button variant="ghost" onClick={() => history.push(`/surveys/${surveyId}/edit`)} style={{ padding: '6px 14px', fontSize: 13 }}>
        <Pencil size={14} /> {t('survey.editQuestions')}
      </Button>
    </div>
  );

  if (!questions || questions.length === 0) {
    return (
      <Container>
        <Empty>{t('survey.noQuestions')}</Empty>
        {editAction}
      </Container>
    );
  }

  return (
    <Container>
      {questions.map((q, i) => {
        const color = typeColors[q.questionType] || '#0291EF';
        const hasOptions = TYPES_WITH_OPTIONS.includes(q.questionType);

        return (
          <React.Fragment key={q.id}>
            {q.sectionTitle && <SectionTitle>{q.sectionTitle}</SectionTitle>}
            <QuestionCard $color={color}>
              <QuestionHeader>
                <QuestionIndex>{i + 1}.</QuestionIndex>
                <QuestionText>{q.questionText}</QuestionText>
                <TypeBadge $color={color}>{t(`survey.${q.questionType}`)}</TypeBadge>
                {q.isRequired && <TypeBadge $color="#dc2626">{t('survey.required')}</TypeBadge>}
              </QuestionHeader>
              {hasOptions && q.options?.length > 0 && (
                <OptionList>
                  {q.options.map((opt) => (
                    <OptionItem key={opt.id}>
                      <OptionDot $color={color} />
                      {opt.label}
                    </OptionItem>
                  ))}
                </OptionList>
              )}
            </QuestionCard>
          </React.Fragment>
        );
      })}

      {editAction}
    </Container>
  );
}
